import { Component, Watch } from 'vue-property-decorator';
import EditorBase from './EditorBase';
import { Selector, StatusSwitch } from 'streamlabs-beaker';
import Position from './settings/Position.vue';
import TextSettings from './settings/TextSettings.vue';
import WebcamSettings from './settings/WebcamSettings.vue';
import ImageSettings from './settings/ImageSettings.vue';
import SLWidgetSettings from './settings/SLWidgetSettings.vue';
import CustomComponentSettings from './settings/CustomComponentSettings.vue';
import VideoSettings from './settings/VideoSettings.vue';
import { ISelector } from '@/helpers/utils';
import ConfirmationModal from '@/components/modals/ConfirmationModal.vue';
import { EComponentType, IComponent, IScene } from '@/helpers/editor';
import { cloneDeep } from 'lodash';
import { ESocketEvent } from '@/helpers/socket';
import StreamLabelSettings from './settings/StreamLabelSettings.vue';
import GameCaptureSettings from './settings/GameCaptureSettings.vue';
import { Debounce } from 'lodash-decorators';

@Component({
  components: {
    SSelector: Selector,
    SStatusSwitch: StatusSwitch,
    Position,
    ConfirmationModal,
  },
})
export default class Settings extends EditorBase {
  resolutionOption: ISelector = null;

  mounted() {
    this.setResolution();
  }

  @Watch('selectedProject')
  setResolution() {
    const currentResolution = this.selectedProject?.settings?.resolution;
    if (currentResolution) {
      this.resolutionOption = this.availableResolution.find(
        (resolution) => resolution.value === currentResolution,
      );
    }
  }

  get availableResolution(): ISelector[] {
    return this.streamResolutions.filter((resolution) => {
      return this.allowedStreamResolutions.find((res) => res.value === resolution.value);
    })
      .map((resolution): ISelector => {
        return {
          label: resolution.label,
          value: resolution.value,
        };
      });
  }

  get settingsTemplate() {
    if (!this.selectedComponent) return;

    switch (this.selectedComponent.type) {
      case EComponentType.text:
        return TextSettings;
      case EComponentType.image:
        return ImageSettings;
      case EComponentType.video:
        return VideoSettings;
      case EComponentType.alertBox:
      case EComponentType.eventList:
      case EComponentType.chat:
        return SLWidgetSettings;
      case EComponentType.customComponent:
        return CustomComponentSettings;
      case EComponentType.webcam:
        return WebcamSettings;
      case EComponentType.streamLabel:
        return StreamLabelSettings;
      case EComponentType.gameCapture:
        return GameCaptureSettings;
      default:
        return null;
    }
  }

  async onResolutionChange() {
    const resolutionSettings = { settings: { resolution: this.resolutionOption.value } };
    const previousResolution: number = this.projectHeight;
    const currentResolution: number = parseInt(this.resolutionOption.value as string);
    const scale = currentResolution / previousResolution;
    const projectClone = cloneDeep(this.selectedProject);

    projectClone.scenes.forEach((scene: IScene) => {
      scene.components = scene.components.map((component: IComponent) => {
        const sizePosition = {
          x: Math.round(component.x * scale),
          y: Math.round(component.y * scale),
          w: Math.round(component.w * scale),
          h: Math.round(component.h * scale),
        };
        return { ...component, ...sizePosition };
      });

      // set current one in editor
      if (scene.uuid === this.$route.params.sceneUuid) {
        this.components = scene.components;
      }
    });

    this.selectedProject = { ...projectClone, ...resolutionSettings };

    await this.debouncedSaveProject();
    this.hasUnsavedChange = false;
    this.sendSocketEvent(ESocketEvent.updateScene);
  }

  get deleteText() {
    return this.selectedProject?.scenes?.length > 1 ? 'Delete Scene' : 'Delete Project';
  }


  get verticalCanvasVisibilityIcon(): string {
    return this.showVerticalCanvas ? 'icon-view' : 'icon-hide';
  }

  deleteScene() {
    if (this.selectedProject.scenes.length === 1) {
      this.openProjectRemovalConfirmationModal(this.selectedProject);
      return;
    }

    this.openSceneRemovalConfirmationModal(this.selectedScene);
  }

  toggleShowVerticalCanvas() {
    this.showVerticalCanvas = !this.showVerticalCanvas;
    const settings = cloneDeep(this.selectedProject.settings);
    settings.showVerticalCanvas = this.showVerticalCanvas;
    this.selectedProject = { ...this.selectedProject, ...{ settings } };
    this.debouncedSaveProject(false);
  }

  @Debounce(1000)
  debouncedSaveProject(saveScene = true) {
    this.saveProject(saveScene);
  }
}
